import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import Tooltip from "PFComponents/tooltip/tooltip";
import { Typography } from "PFComponents/typography";
import { IconName } from "PFTheme/graphics/icons";
import { CustomValueState } from "PFTypes";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useBulkActions } from "../hooks/use_bulk_actions";
import css from "./bulk_actions.module.scss";

export const BulkActions = () => {
  const { t } = useTranslation("skillsManagement", { keyPrefix: "bulkActions" });
  const {
    selectedSkills,
    selectNone,
    blacklistSelected,
    approveSelected,
    exportSelected,
    resetSelected,
    mergeSelected
  } = useBulkActions();

  const isAnyGlobalSkillSelected = useMemo(
    () => selectedSkills.some(({ global_id: globalId }) => globalId !== null),
    [selectedSkills]
  );

  const areSelectedOnly = useCallback(
    (...states: CustomValueState[]) => !selectedSkills.some((skill) => !states.includes(skill.state)),
    [selectedSkills]
  );

  const areOnlyMergedOrApprovedSkillsSelected = areSelectedOnly(
    CustomValueState.Approved,
    CustomValueState.Merged
  );

  const areOnlyMergedOrBlockedSkillsSelected = areSelectedOnly(
    CustomValueState.Blocked,
    CustomValueState.Merged
  );

  const areOnlyBlockedSkillsSelected = areSelectedOnly(CustomValueState.Blocked);

  if (selectedSkills.length === 0) {
    return null;
  }

  return (
    <div className={css.mainContainer}>
      <div className={css.counterContainer}>
        <Button kind="blank" onClick={selectNone}>
          <Icon size="sm" name="cross" />
        </Button>
        <Typography variant="bodyBold" tag="span">
          {t("selectedCounter", {
            count: selectedSkills.length
          })}
        </Typography>
      </div>
      <div className={css.actionsContainer}>
        <BulkAction
          onClick={approveSelected}
          iconName="check"
          title={
            areOnlyMergedOrApprovedSkillsSelected
              ? t("titles.cannotApprove", { count: selectedSkills.length })
              : t("titles.approve")
          }
          disabled={areOnlyMergedOrApprovedSkillsSelected}
        />
        <BulkAction
          onClick={blacklistSelected}
          iconName="forbidden"
          title={
            areOnlyMergedOrBlockedSkillsSelected
              ? t("titles.cannotBlock", { count: selectedSkills.length })
              : t("titles.block")
          }
          disabled={areOnlyMergedOrBlockedSkillsSelected}
        />
        <BulkAction
          onClick={mergeSelected}
          iconName="merge"
          title={
            isAnyGlobalSkillSelected
              ? t("titles.cannotMergeGlobalSkills")
              : areOnlyMergedOrBlockedSkillsSelected
              ? t("titles.cannotMerge", { count: selectedSkills.length })
              : t("titles.merge")
          }
          disabled={areOnlyMergedOrBlockedSkillsSelected || isAnyGlobalSkillSelected}
        />
        <BulkAction onClick={exportSelected} iconName="export" title={t("titles.export")} />
        <BulkAction
          onClick={resetSelected}
          iconName="history"
          title={t("titles.reset")}
          disabled={isAnyGlobalSkillSelected && !areOnlyBlockedSkillsSelected}
        />
      </div>
    </div>
  );
};

type BulkActionProps = {
  onClick: () => void;
  iconName: IconName;
  title: string;
  disabled?: boolean;
};

export const BulkAction = ({ onClick, iconName, title, disabled }: BulkActionProps) => (
  <Tooltip content={title} placement="top">
    <span>
      <Button kind="tertiary" icon={iconName} onClick={onClick} disabled={disabled} />
    </span>
  </Tooltip>
);
